import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_VEHICLES } from '../../../../gql/queries/vehicles';
import {
  GET_VEHICLE_CATEGORIES,
  GET_VEHICLE_CATEGORY_BY_ID,
} from '../../../../gql/queries/vehicleCategory';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { GET_CONCESSION_BY_ID } from '../../../../gql/queries/geoPositions/roads';
import { FETCH_TEAMS } from '../../../../gql/queries/users';
// Utils
import { AuthContextType } from '../../../../hooks/useAuth';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';

// Constants
import { formVehicleManagementNaming } from '../../../../components/forms/AddVehicleManagementForm/constants';

const loadTableData = async (
  userData: AuthContextType | null,
  teamsData: { id: string; name: string }[]
) => {
  try {
    if (userData?.user) {
      let vehiclesToReturn;

      const {
        data: { vehicles },
      } = await client.query({
        query: FETCH_VEHICLES,
        fetchPolicy: 'cache-first',
      });

      if (!vehicles || vehicles.length === 0) {
        const {
          data: { vehicles },
        } = await client.query({
          query: FETCH_VEHICLES,
        });

        vehiclesToReturn = vehicles;
      } else {
        vehiclesToReturn = vehicles;
      }

      // Create a map for quick team lookup
      const teamsMap = new Map(teamsData.map((team) => [team.id, team.name]));

      return Promise.all(
        vehiclesToReturn.map(
          async (vehicle: {
            id: string;
            plate: string;
            model: string;
            isActive: boolean;
            isTrailer: boolean;
            category: string;
            ownership: string;
            usage: string;
            vehicleCategoryIDs: string[];
            concessionIDs: string[];
            teamIDs: string[];
          }) => {
            await client.query({
              query: GET_VEHICLE_CATEGORIES,
            });

            let categoryName;

            // Only query category if vehicleCategoryIDs exists and has at least one ID
            if (
              vehicle?.vehicleCategoryIDs?.length > 0 &&
              vehicle.vehicleCategoryIDs[0]
            ) {
              const {
                data: { vehicleCategory },
              } = await client.query({
                query: GET_VEHICLE_CATEGORY_BY_ID,
                variables: {
                  id: vehicle.vehicleCategoryIDs[0],
                },
                fetchPolicy: 'cache-first',
              });
              categoryName = vehicleCategory.name;
            }

            let concessionName;

            if (
              vehicle?.concessionIDs?.length > 0 &&
              vehicle.concessionIDs[0]
            ) {
              const {
                data: { concession },
              } = await client.query({
                query: GET_CONCESSION_BY_ID,
                variables: {
                  id: vehicle.concessionIDs[0],
                },
              });

              concessionName = concession.name;
            }

            // ✅ M2M: Resolve team IDs to team names
            const teamNames = (vehicle.teamIDs || [])
              .map((teamId) => teamsMap.get(teamId))
              .filter((name): name is string => !!name);

            return {
              id: vehicle.id,
              plate: vehicle.plate,
              model: vehicle.model,
              isTrailer: vehicle.isTrailer,
              state: vehicle.isActive ? 'active' : 'inactive',
              isActive: vehicle.isActive,
              ownership: vehicle.ownership,
              usage: vehicle.usage,
              category: categoryName,
              concession: concessionName,
              teamIDs: vehicle.teamIDs, // Keep IDs for reference
              teams: teamNames, // ✅ M2M: Team names for display
            };
          }
        )
      );
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('> User management loading error:', err);
    return true;
  }
};

export const VehiclesManagementLoader: (
  vehicleData: AuthContextType | null
) => LoaderFunction | undefined = (vehicleData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // ✅ Fetch teams FIRST so we can resolve team names in loadTableData
  const {
    data: { teams },
  } = await client.query({
    query: FETCH_TEAMS,
  });

  const data = (await loadTableData(vehicleData, teams)) as [];
  const activeVehicles = data?.filter(
    (vehicle: { isActive: boolean }) => vehicle?.isActive
  );
  const inactiveVehicles = data?.filter(
    (vehicle: { isActive: boolean }) => !vehicle?.isActive
  );
  const {
    data: { vehicleCategories },
  } = await client.query({
    query: GET_VEHICLE_CATEGORIES,
  });

  const {
    data: { concessions },
  } = await client.query({
    query: GET_CONCESSIONS,
  });

  let cacheData: any =
    sessionStorage.getItem(
      formVehicleManagementNaming.NEW_VEHICLE_MANAGEMENT_FORM
    ) || {};

  if (Object.keys(cacheData).length > 0) {
    cacheData = JSON.parse(decompressSessionStorage(cacheData as string));
  } else {
    // ✅ Valores por defecto cuando no hay cache
    cacheData = {
      id: '',
      plate: '',
      model: '',
      category: '',
      isActive: true,
      ownership: 'INTERNAL',
      usage: 'TRANSPORT',
      isTrailer: false,
    };
  }

  return {
      activeVehicles,
      inactiveVehicles,
      cacheData,
      vehicleCategories,
      concessions,
      teams,
    };
};

  return { data: load() };
};
